/**
 * Modèle de domaine Bala'App — partagé entre l'API, l'app Staff et l'app Parents.
 * Toutes les dates sont des chaînes ISO 8601 (UTC pour les horodatages,
 * `YYYY-MM-DD` pour les dates civiles comme les anniversaires).
 * Tous les montants sont en centimes (entiers) pour éviter les erreurs de flottant.
 */

export type Id = string;
export type IsoDateTime = string;
export type IsoDate = string;

// ---------------------------------------------------------------------------
// Permissions & rôles
// ---------------------------------------------------------------------------

/** Domaines protégés de l'application. */
export const RESOURCES = [
  'baladins',
  'animateurs',
  'calendar',
  'attendance',
  'tasks',
  'finances',
  'games',
  'inventaire',
  'uniformes',
  'photos',
  'documents',
  'messages',
  'admin',
  'settings',
] as const;
export type Resource = (typeof RESOURCES)[number];

/**
 * Niveaux d'accès cumulatifs : `admin` implique `write`, qui implique `read`.
 * Voir `permissions.ts` pour la comparaison.
 */
export const ACCESS_LEVELS = ['none', 'read', 'write', 'admin'] as const;
export type AccessLevel = (typeof ACCESS_LEVELS)[number];

/**
 * Capacités ponctuelles qui ne se réduisent pas à un niveau d'accès.
 * `tasks.validate` / `tasks.revise` correspondent aux étapes du workflow
 * réservées à Baygon dans le cahier des charges.
 */
export const CAPABILITIES = [
  'tasks.validate',
  'tasks.revise',
  'photos.publish',
  'messages.publish',
  'finances.export',
  'attendance.export',
  'trash.restore',
  'backup.manage',
  /** Créer, renommer et supprimer les catégories (finances et jeux). */
  'categories.manage',
] as const;
export type Capability = (typeof CAPABILITIES)[number];

export type PermissionMap = Partial<Record<Resource, AccessLevel>>;

export interface Role {
  id: Id;
  name: string;
  description: string;
  /** Niveau d'accès par domaine. Un domaine absent équivaut à `none`. */
  permissions: PermissionMap;
  capabilities: Capability[];
  /** Les rôles système ne peuvent pas être supprimés (Baygon, Animateur, Lecture seule). */
  isSystem: boolean;
  /** Rang d'affichage et de tri (plus petit = plus haut placé). */
  rank: number;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

// ---------------------------------------------------------------------------
// Comptes
// ---------------------------------------------------------------------------

export type AccountStatus = 'invited' | 'active' | 'disabled';

/** Animateur (compte de l'app Staff). */
export interface User {
  id: Id;
  email: string;
  firstName: string;
  lastName: string;
  /** Totem scout, affiché en priorité dans l'interface quand il est présent. */
  totem: string | null;
  birthDate: IsoDate | null;
  phone: string | null;
  address: string | null;
  avatarFileId: Id | null;
  roleId: Id;
  status: AccountStatus;
  /** Notes internes visibles par les animateurs (allergies, régime, etc.). */
  importantInfo: string | null;
  allergies: string | null;
  /** Drapeau Super Admin (exclusif Baigon) pour superviser l'ensemble des apps. */
  isSuperAdmin?: boolean;
  lastLoginAt: IsoDateTime | null;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  deletedAt: IsoDateTime | null;
}

/** Compte parent (app Parents). Associé à un ou plusieurs baladins. */
export interface ParentAccount {
  id: Id;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  status: AccountStatus;
  childIds: Id[];
  notificationPrefs: NotificationPrefs;
  lastLoginAt: IsoDateTime | null;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  deletedAt: IsoDateTime | null;
}

/**
 * Contact du répertoire public visible par les parents.
 * Géré manuellement par les administrateurs (Baygon) sans être lié
 * aux comptes du Staff (permet d'inclure des Chefs d'Unité, etc.).
 */
export interface PublicContact {
  id: Id;
  name: string;
  role: string;
  phoneNumber: string;
  contactReason: string;
  messengerId?: string | null;
  messenger_id?: string | null;
  displayOrder: number;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

/**
 * Catégorie d'un créneau du planning Bala-Camp (camps et hikes).
 * Créée depuis l'onglet Baygon ; chaque option dit quelles rubriques la fiche
 * d'un créneau de cette catégorie propose (responsables, matériel…).
 * Une catégorie retirée reste en base (`archived`) pour que les créneaux qui
 * l'utilisent déjà gardent leur libellé et leur couleur.
 */
export interface CampActivityCategory {
  id: Id;
  label: string;
  /** Couleur hexadécimale `#rrggbb` de la pastille. */
  color: string;
  requiresResponsible: boolean;
  hasMaterial: boolean;
  hasPitch: boolean;
  hasRules: boolean;
  hasWeatherPlan: boolean;
  sortOrder: number;
  archived: boolean;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

/** Baladin (animé). */
export interface Baladin {
  id: Id;
  firstName: string;
  lastName: string;
  birthDate: IsoDate;
  /**
   * Contacts des parents — jusqu'à deux, un par parent. Distincts des comptes
   * de connexion à l'app Parents (`ParentAccount`, `parent_children`) : une
   * famille a ces numéros sur la fiche bien avant, ou même sans jamais avoir
   * de compte applicatif.
   */
  parent1Email: string | null;
  parent1Phone: string | null;
  parent2Email: string | null;
  parent2Phone: string | null;
  address: string | null;
  allergies: string | null;
  medicalInfo: string | null;
  importantInfo: string | null;
  photoConsent: boolean;
  /**
   * Documents administratifs rendus par la famille. Faux par défaut : tant que
   * personne n'a coché, le papier n'est pas arrivé — l'inverse aurait déclaré
   * en règle toute une section.
   */
  registrationFormReturned: boolean;
  healthFormReturned: boolean;
  parentalAuthorizationReturned: boolean;
  charterSigned: boolean;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  deletedAt: IsoDateTime | null;
}

export interface NotificationPrefs {
  newMessage: boolean;
  newActivity: boolean;
  activityChanged: boolean;
  activityCancelled: boolean;
  newPhotos: boolean;
  attendanceReminder: boolean;
  channel: { push: boolean; email: boolean };
}

export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
  newMessage: true,
  newActivity: true,
  activityChanged: true,
  activityCancelled: true,
  newPhotos: true,
  attendanceReminder: true,
  channel: { push: true, email: true },
};

// ---------------------------------------------------------------------------
// Calendrier
// ---------------------------------------------------------------------------

export const ACTIVITY_TYPES = [
  'reunion_bala',
  'reunion_surprise',
  'reunion_staff',
  'reunion_parents',
  'activite_staff',
  'activite_unite',
  'hike',
  'camp',
] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];

export const ACTIVITY_TYPE_LABELS: Record<ActivityType, string> = {
  reunion_bala: 'Réunion Bala',
  reunion_surprise: 'Réunion Surprise',
  reunion_staff: 'Réunion Staff',
  reunion_parents: 'Réunion Parents',
  activite_staff: 'Activité Staff',
  activite_unite: 'Activité Unité',
  hike: 'Hike',
  camp: 'Camp',
};

/**
 * Matrice métier des types d'événements (présences / RSVP parents / jeux) :
 *
 * | Type              | Présences baladins | RSVP / rappel parents | Module Jeu | Covoiturage |
 * |-------------------|--------------------|-----------------------|------------|-------------|
 * | reunion_bala      | oui (+ animateurs) | oui                   | oui        | non         |
 * | reunion_surprise  | oui (+ animateurs) | oui                   | oui        | oui         |
 * | reunion_staff     | non (animateurs)   | non                   | non        | non         |
 * | reunion_parents   | non (aucune)       | non                   | non        | non         |
 * | activite_staff    | non (animateurs)   | non                   | non        | non         |
 * | activite_unite    | non (animateurs)*  | non                   | non        | non         |
 * | hike / camp       | oui (+ animateurs) | oui                   | non        | non         |
 *
 * *À l'unité, les animés sont présents mais les présences section ne les
 * encodent pas — elles sont relevées à l'échelle de l'unité. Pas de présences
 * prévisionnelles section non plus.
 */

/** Types visibles par défaut dans l'app Parents (calendrier + notifications). */
export const PARENT_VISIBLE_TYPES: ActivityType[] = [
  'reunion_bala',
  'reunion_surprise',
  'reunion_parents',
  'activite_unite',
  'hike',
  'camp',
];

/**
 * Types pour lesquels une demande / un rappel de présence aux parents est
 * activé par défaut (`requestRsvp`).
 */
export const PARENT_RSVP_TYPES: ActivityType[] = [
  'reunion_bala',
  'reunion_surprise',
  'hike',
  'camp',
];

/** Types qui exposent le module Jeu (association de jeux à l'activité). */
export const GAME_MODULE_TYPES: ActivityType[] = ['reunion_bala', 'reunion_surprise'];

/**
 * Types avec module covoiturage (réponses parents + répartition staff).
 * Réservé aux réunions surprise pour l'instant.
 */
export const CARPOOL_TYPES: ActivityType[] = ['reunion_surprise'];

/**
 * Types sans présence d'animé côté section : seule la grille des animateurs
 * a un sens. Couvre réunion/activité staff et activité d'unité.
 */
export const STAFF_ONLY_TYPES: ActivityType[] = ['reunion_staff', 'activite_staff', 'activite_unite'];

/** Types sans encodage de présences (ni enfants ni animateurs). */
export const NO_ATTENDANCE_TYPES: ActivityType[] = ['reunion_parents'];

/** `true` si l'activité gère des présences (enfants ou staff). */
export function hasAttendance(type: ActivityType): boolean {
  return !NO_ATTENDANCE_TYPES.includes(type);
}

/** `true` si l'activité n'encode pas les présences des animés. */
export function isStaffOnlyActivity(type: ActivityType): boolean {
  return STAFF_ONLY_TYPES.includes(type);
}

/** `true` si les parents doivent pouvoir répondre à une demande de présence. */
export function activityRequestsParentRsvp(type: ActivityType): boolean {
  return PARENT_RSVP_TYPES.includes(type);
}

/** `true` si le module Jeu est disponible pour ce type. */
export function activityHasGameModule(type: ActivityType): boolean {
  return GAME_MODULE_TYPES.includes(type);
}

/** `true` si le module covoiturage est disponible pour ce type. */
export function activityHasCarpoolModule(type: ActivityType): boolean {
  return CARPOOL_TYPES.includes(type);
}

/** Types qui exposent le générateur d'équipes (présences baladins encodées). */
export const TEAM_GENERATOR_TYPES: ActivityType[] = [
  'reunion_bala',
  'reunion_surprise',
];

/** `true` si le générateur d'équipes est disponible pour ce type. */
export function activityHasTeamGenerator(type: ActivityType): boolean {
  return TEAM_GENERATOR_TYPES.includes(type);
}

/** Types qui génèrent automatiquement un album photo et une feuille de présences. */
export const ALBUM_TYPES: ActivityType[] = [
  'reunion_bala',
  'reunion_surprise',
  'hike',
  'camp',
  'activite_unite',
];

/**
 * Deux états. « Annulée » a été retiré : une activité qui n'a pas lieu se
 * supprime (elle part à la corbeille et les parents sont prévenus), elle ne
 * reste pas au calendrier barrée. Les activités qui portaient ce statut sont
 * repassées en brouillon par migration.
 */
export type ActivityStatus = 'draft' | 'confirmed';

export interface ChecklistItem {
  id: Id;
  label: string;
  done: boolean;
  assigneeId: Id | null;
  /** Issu d'un modèle de réunion ou de la check-list automatique. */
  auto: boolean;
  order: number;
}

/** Lieu et adresse préenregistrés des locaux scouts (point de rendez-vous par défaut). */
export const SCOUT_LOCAUX_NAME = 'Locaux scouts';
export const SCOUT_LOCAUX_ADDRESS = 'Chemin des Maies 121, 6600 Bastogne';

export interface Activity {
  id: Id;
  type: ActivityType;
  title: string;
  description: string | null;
  startAt: IsoDateTime;
  endAt: IsoDateTime;
  allDay: boolean;
  /** Lieu du jeu enregistré — visible uniquement par les animateurs. */
  placeId: Id | null;
  /** Lieu du jeu libre — visible uniquement par les animateurs. */
  locationText: string | null;
  /** Nom du lieu de rendez-vous pour les parents (ex. « Locaux scouts »). */
  meetingLocationName: string | null;
  /** Adresse postale du lieu de rendez-vous pour les parents (copiable dans le GPS). */
  meetingLocationAddress: string | null;
  responsibleIds: Id[];
  gameIds: Id[];
  documentIds: Id[];
  checklist: ChecklistItem[];
  /** Matériel à prévoir par les parents (affiché dans l'app Parents). */
  materialForFamilies: string[];
  /** Matériel à prévoir par le staff. */
  materialForStaff: string[];
  status: ActivityStatus;
  /** Forcer la visibilité parents indépendamment du type. */
  parentVisible: boolean | null;
  /** Demander une réponse de présence aux parents. */
  requestRsvp: boolean;
  createdBy: Id;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  deletedAt: IsoDateTime | null;
}

export interface Place {
  id: Id;
  name: string;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  notes: string | null;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  deletedAt: IsoDateTime | null;
}

export interface MeetingTemplate {
  id: Id;
  name: string;
  description: string | null;
  type: ActivityType;
  durationMinutes: number;
  checklist: Array<Pick<ChecklistItem, 'label'>>;
  materialForStaff: string[];
  materialForFamilies: string[];
  gameIds: Id[];
  createdBy: Id;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

// ---------------------------------------------------------------------------
// Présences
// ---------------------------------------------------------------------------

export type PersonType = 'baladin' | 'animateur';

/**
 * Deux états, et deux seulement. « En retard » et « Excusé » ont été retirés :
 * la section ne les utilisait pas, et ils compliquaient le calcul du taux de
 * présence — le retard comptait comme une présence, l'excusé sortait du
 * dénominateur. Les lignes qui les portaient ont été converties en « Présent »
 * par migration.
 */
export const ATTENDANCE_STATUSES = ['present', 'absent'] as const;
export type AttendanceStatus = (typeof ATTENDANCE_STATUSES)[number];

export const ATTENDANCE_STATUS_LABELS: Record<AttendanceStatus, string> = {
  present: 'Présent',
  absent: 'Absent',
};

/** Présence réelle, encodée par un animateur. */
export interface AttendanceRecord {
  id: Id;
  activityId: Id;
  personType: PersonType;
  personId: Id;
  status: AttendanceStatus;
  /**
   * Note libre. Côté animateurs, c'est le champ « raison de l'absence » qui
   * apparaît sous la ligne dès que « Absent » est coché.
   */
  comment: string | null;
  recordedBy: Id;
  recordedAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

export const RSVP_STATUSES = ['present', 'absent', 'uncertain'] as const;
export type RsvpStatus = (typeof RSVP_STATUSES)[number];

export const RSVP_STATUS_LABELS: Record<RsvpStatus, string> = {
  present: 'Présent',
  absent: 'Absent',
  uncertain: 'Incertain',
};

/**
 * Présence prévisionnelle indiquée par un parent. Sert uniquement aux prévisions
 * et ne remplace jamais un `AttendanceRecord`.
 */
export interface Rsvp {
  id: Id;
  activityId: Id;
  baladinId: Id;
  status: RsvpStatus;
  comment: string | null;
  answeredByParentId: Id | null;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

export interface AttendanceStats {
  total: number;
  present: number;
  absent: number;
  /** Taux de présence en pourcentage, présents / total, arrondi à 0,1 %. */
  rate: number;
}

// ---------------------------------------------------------------------------
// Covoiturage (réunions surprise)
// ---------------------------------------------------------------------------

export const CARPOOL_RESPONSE_STATUSES = [
  'aller_seulement',
  'retour_seulement',
  'aller_retour',
  'aucun',
] as const;
export type CarpoolResponseStatus = (typeof CARPOOL_RESPONSE_STATUSES)[number];

export const CARPOOL_RESPONSE_STATUS_LABELS: Record<CarpoolResponseStatus, string> = {
  aller_seulement: 'Aller seulement',
  retour_seulement: 'Retour seulement',
  aller_retour: 'Aller-retour',
  aucun: 'Aucun',
};

export const CARPOOL_DEPARTURE_TYPES = ['locaux', 'autre'] as const;
export type CarpoolDepartureType = (typeof CARPOOL_DEPARTURE_TYPES)[number];

export const CARPOOL_DEPARTURE_TYPE_LABELS: Record<CarpoolDepartureType, string> = {
  locaux: 'Locaux scouts',
  autre: 'Autre endroit',
};

export const CARPOOL_JOURNEY_TYPES = ['aller', 'retour'] as const;
export type CarpoolJourneyType = (typeof CARPOOL_JOURNEY_TYPES)[number];

export const CARPOOL_JOURNEY_TYPE_LABELS: Record<CarpoolJourneyType, string> = {
  aller: 'Aller',
  retour: 'Retour',
};

/** Proposition de covoiturage d'un parent pour un enfant sur une activité. */
export interface CarpoolResponse {
  id: Id;
  activityId: Id;
  parentId: Id;
  baladinId: Id;
  status: CarpoolResponseStatus;
  seatsAller: number;
  seatsRetour: number;
  departureType: CarpoolDepartureType | null;
  customLocation: string | null;
  comment: string | null;
  phone: string | null;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

/** Affectation staff : un passager dans la voiture d'un conducteur (aller ou retour). */
export interface CarpoolAssignment {
  id: Id;
  activityId: Id;
  journeyType: CarpoolJourneyType;
  driverParentId: Id;
  passengerBaladinId: Id;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

// ---------------------------------------------------------------------------
// Objets perdus (Vinted Scout)
// ---------------------------------------------------------------------------

export const LOST_ITEM_STATUSES = ['disponible', 'reclame', 'restitue'] as const;
export type LostItemStatus = (typeof LOST_ITEM_STATUSES)[number];

export const LOST_ITEM_STATUS_LABELS: Record<LostItemStatus, string> = {
  disponible: 'Disponible',
  reclame: 'Réclamé',
  restitue: 'Restitué',
};

export interface LostItem {
  id: Id;
  /** Photo de l'objet, stockée comme les autres fichiers de l'app. */
  fileId: Id;
  description: string;
  status: LostItemStatus;
  claimedByParentId: Id | null;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

// ---------------------------------------------------------------------------
// Onglet Dev : bugs et idées
// ---------------------------------------------------------------------------

export const DEV_TICKET_CATEGORIES = ['bug', 'idea'] as const;
export type DevTicketCategory = (typeof DEV_TICKET_CATEGORIES)[number];

export const DEV_TICKET_CATEGORY_LABELS: Record<DevTicketCategory, string> = {
  bug: 'Bug',
  idea: 'Idée',
};

export const DEV_TICKET_STATUSES = ['todo', 'in_progress', 'done'] as const;
export type DevTicketStatus = (typeof DEV_TICKET_STATUSES)[number];

/**
 * Le libellé du dernier statut change avec la catégorie : un bug est
 * « Résolu », une idée est « Faite ». Même colonne, deux mots justes.
 */
export const DEV_TICKET_STATUS_LABELS: Record<DevTicketCategory, Record<DevTicketStatus, string>> = {
  bug: { todo: 'À faire', in_progress: 'En cours', done: 'Résolu' },
  idea: { todo: 'À faire', in_progress: 'En cours', done: 'Faite' },
};

export const DEV_TICKET_PRIORITIES = ['low', 'medium', 'high'] as const;
export type DevTicketPriority = (typeof DEV_TICKET_PRIORITIES)[number];

export const DEV_TICKET_PRIORITY_LABELS: Record<DevTicketPriority, string> = {
  low: 'Basse',
  medium: 'Moyenne',
  high: 'Haute',
};

export const DEV_TICKET_AUTHOR_ROLES = ['parent', 'staff'] as const;
export type DevTicketAuthorRole = (typeof DEV_TICKET_AUTHOR_ROLES)[number];

export interface DevTicket {
  id: Id;
  category: DevTicketCategory;
  title: string;
  description: string;
  /** Déduit de la session à l'envoi, jamais de ce que le client déclare. */
  authorRole: DevTicketAuthorRole;
  authorName: string | null;
  status: DevTicketStatus;
  priority: DevTicketPriority;
  /** Page d'où part le signalement, expurgée de ses paramètres sensibles. */
  currentUrl: string;
  userAgent: string;
  isRead: boolean;
  /** Ce qu'on écrit dans le changelog, à défaut du titre du ticket. */
  releaseNotes: string | null;
  doneAt: IsoDateTime | null;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

// ---------------------------------------------------------------------------
// Générateur d'équipes
// ---------------------------------------------------------------------------

/** Paire d'enfants qui ne doivent pas se retrouver dans la même équipe. */
export interface TeamIncompatibility {
  id: Id;
  baladinAId: Id;
  baladinBId: Id;
  createdAt: IsoDateTime;
}

/** Affectation d'un enfant à une équipe pour une activité. */
export interface TeamAssignment {
  id: Id;
  activityId: Id;
  /** Index 0-based de l'équipe (0 → équipe 1 à l'écran). */
  teamIndex: number;
  baladinId: Id;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

export const TEAM_COUNTS = [
  2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
] as const;
export type TeamCount = (typeof TEAM_COUNTS)[number];

// ---------------------------------------------------------------------------
// Tâches
// ---------------------------------------------------------------------------

/**
 * « Terminé » et « À valider » désignaient le même moment du travail : une tâche
 * que son auteur considère finie et que le Baygon n'a pas encore relue. Deux
 * colonnes pour un seul état obligeaient à passer manuellement de l'une à
 * l'autre, et laissaient des tâches finies dormir sans jamais arriver au
 * Baygon. Il ne reste que `to_validate` ; l'interface l'appelle « Terminées »
 * pour l'animateur qui la dépose et « À valider » pour le Baygon qui la relit.
 */
export const TASK_STATUSES = [
  'todo',
  'doing',
  'to_validate',
  'validated',
  'to_revise',
] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

/** Libellés canoniques ; l'écran des tâches renomme `to_validate` selon le rôle. */
export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  todo: 'À faire',
  doing: 'En cours',
  to_validate: 'À valider',
  validated: 'Validé',
  to_revise: 'À modifier',
};

/** Statuts que seul un utilisateur disposant de la capacité Baygon peut appliquer. */
export const BAYGON_ONLY_STATUSES: TaskStatus[] = ['validated', 'to_revise'];

export const TASK_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: 'Basse',
  normal: 'Normale',
  high: 'Haute',
  urgent: 'Urgente',
};

export interface TaskComment {
  id: Id;
  taskId: Id;
  authorId: Id;
  body: string;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  deletedAt: IsoDateTime | null;
}

export interface TaskItem {
  id: Id;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  assigneeIds: Id[];
  dueDate: IsoDate | null;
  activityId: Id | null;
  attachmentIds: Id[];
  /** Décalage en jours avant l'échéance pour le rappel automatique. */
  reminderDaysBefore: number | null;
  reminderSentAt: IsoDateTime | null;
  order: number;
  createdBy: Id;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  deletedAt: IsoDateTime | null;
}

// ---------------------------------------------------------------------------
// Finances
// ---------------------------------------------------------------------------

export type TransactionKind = 'income' | 'expense';

export interface FinanceCategory {
  id: Id;
  name: string;
  kind: TransactionKind | 'both';
  color: string;
  icon: string | null;
  archived: boolean;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

export interface Transaction {
  id: Id;
  kind: TransactionKind;
  /** Montant en centimes, toujours positif ; le signe vient de `kind`. */
  amountCents: number;
  date: IsoDate;
  label: string;
  description: string | null;
  categoryId: Id | null;
  activityId: Id | null;
  receiptFileIds: Id[];
  /** Rapproché avec le relevé bancaire. */
  reconciled: boolean;
  createdBy: Id;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  deletedAt: IsoDateTime | null;
}

export interface Budget {
  id: Id;
  categoryId: Id;
  /** Année scolaire au format `2026-2027`. */
  period: string;
  amountCents: number;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

// ---------------------------------------------------------------------------
// Jeux
// ---------------------------------------------------------------------------

export type GamePlace = 'inside' | 'outside' | 'both';
export const GAME_PLACES = ['inside', 'outside', 'both'] as const;

export const GAME_PLACE_LABELS: Record<GamePlace, string> = {
  inside: 'Intérieur',
  outside: 'Extérieur',
  both: 'Les deux',
};

export const GAME_WEATHERS = ['sun', 'rain', 'snow', 'any'] as const;
export type GameWeather = (typeof GAME_WEATHERS)[number];

export const GAME_WEATHER_LABELS: Record<GameWeather, string> = {
  sun: 'Beau temps',
  rain: 'Pluie',
  snow: 'Neige',
  any: 'Par tout temps',
};

export interface GameVariant {
  id: Id;
  title: string;
  body: string;
}

export interface GameCategory {
  id: Id;
  name: string;
  color: string;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

export interface Game {
  id: Id;
  name: string;
  minPlayers: number | null;
  maxPlayers: number | null;
  durationMinutes: number | null;
  place: GamePlace;
  weather: GameWeather[];
  /** Matériel de l'unité nécessaire au jeu, pris dans l'inventaire. */
  inventoryItemIds: Id[];
  /** Ce qu'il faudrait acheter pour pouvoir y jouer, en texte libre. */
  materialToBuy: string[];
  categoryIds: Id[];
  description: string | null;
  rules: string | null;
  variants: GameVariant[];
  tags: string[];
  /** Moyenne des évaluations des animateurs, 0 à 5. */
  rating: number | null;
  createdBy: Id;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  deletedAt: IsoDateTime | null;
}

// ---------------------------------------------------------------------------
// Suivi des paiements
// ---------------------------------------------------------------------------

/**
 * Qui doit figurer sur un suivi de paiement.
 *
 * - `children` : les baladins (comportement historique)
 * - `staff` : les animateurs actifs
 * - `both` : les deux listes, cochées séparément
 */
export type PaymentAudience = 'children' | 'staff' | 'both';

export const PAYMENT_AUDIENCES: PaymentAudience[] = ['children', 'staff', 'both'];

/**
 * Ce qu'il y a à payer : une sortie, un camp, les goûters, une cotisation staff…
 *
 * Le prix est unitaire et en centimes, comme partout dans les finances : un
 * montant en euros flottants finit toujours par produire un solde faux de
 * quelques centimes.
 */
/**
 * Montant dû par un enfant sur un suivi à montants variables (ex. uniformes).
 * Absent du suivi « classique » où tout le monde paie le même `unitPriceCents`.
 */
export interface PaymentDue {
  baladinId: Id;
  amountCents: number;
}

/**
 * Cuistot ajouté directement dans un suivi de paiement de séjour (hike / camp).
 * Ne crée aucun compte animateur ailleurs dans l'application.
 */
export interface PaymentCook {
  id: Id;
  name: string;
}

export interface PaymentItem {
  id: Id;
  label: string;
  unitPriceCents: number;
  /**
   * Tarif réduit par enfant lorsqu'une famille inscrit plusieurs enfants
   * (fratrie), utilisé pour les cotisations, hikes et camps.
   */
  siblingPriceCents: number | null;
  /**
   * Tarif spécifique appliqué aux cuistots sur un suivi de paiement staff
   * (hike / camp), à la place du tarif fratrie.
   */
  cookPriceCents: number | null;
  audience: PaymentAudience;
  /** Activité liée : un enfant payé y est compté comme inscrit. */
  activityId: Id | null;
  /** Catégorie de la recette générée à chaque paiement. */
  categoryId: Id | null;
  notes: string | null;
  /**
   * Campagne d'uniformes liée. Quand renseigné, le suivi n'affiche que les
   * enfants présents dans `dues` (ceux qui ont commandé), chacun au prix de
   * sa commande — pas toute la section.
   */
  uniformCampaignId: Id | null;
  /** Montants individuels ; pertinent surtout avec `uniformCampaignId`. */
  dues: PaymentDue[];
  /** Cuistots rattachés uniquement à ce suivi de paiement. */
  cooks: PaymentCook[];
  createdBy: Id;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  deletedAt: IsoDateTime | null;
}

/**
 * Un paiement encaissé. L'absence de ligne vaut « pas payé » : on n'enregistre
 * que ce qui est arrivé, jamais ce qui manque. Exactement un de `baladinId` /
 * `userId` est renseigné selon que le payeur est un enfant ou un animateur.
 */
export interface Payment {
  id: Id;
  paymentItemId: Id;
  baladinId: Id | null;
  userId: Id | null;
  cookId: Id | null;
  /** Montant effectivement encaissé (tarif plein, tarif fratrie, tarif cuistot ou commande uniforme). */
  amountCents: number | null;
  paidAt: IsoDateTime;
  recordedBy: Id;
  /** Recette créée automatiquement, retirée si la case est décochée. */
  transactionId: Id | null;
}

// ---------------------------------------------------------------------------
// Inventaire
// ---------------------------------------------------------------------------

/**
 * Ce dans quoi le matériel est rangé. Le mot « malle » est celui de la section,
 * mais une farde ou une caisse se range de la même façon : c'est le contenant
 * qui compte, pas son nom.
 */
export const TRUNK_KINDS = ['malle', 'caisse', 'boite', 'farde', 'armoire', 'autre'] as const;
export type TrunkKind = (typeof TRUNK_KINDS)[number];

export const TRUNK_KIND_LABELS: Record<TrunkKind, string> = {
  malle: 'Malle',
  caisse: 'Caisse',
  boite: 'Boîte',
  farde: 'Farde',
  armoire: 'Armoire',
  autre: 'Autre',
};

export interface Trunk {
  id: Id;
  name: string;
  kind: TrunkKind;
  /** Où se trouve le contenant : « grenier », « local, étagère du fond »… */
  location: string | null;
  notes: string | null;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  deletedAt: IsoDateTime | null;
}

export interface InventoryItem {
  id: Id;
  name: string;
  quantity: number;
  /** Malle où l'objet est rangé. `null` : possédé mais pas encore classé. */
  trunkId: Id | null;
  notes: string | null;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  deletedAt: IsoDateTime | null;
}

// ---------------------------------------------------------------------------
// Fichiers, photos, documents
// ---------------------------------------------------------------------------

export interface StoredFile {
  id: Id;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  checksum: string;
  uploadedBy: Id;
  createdAt: IsoDateTime;
}

export type PublicationStatus = 'draft' | 'public';

export type AlbumAudience = 'all' | 'participants';

export interface Album {
  id: Id;
  activityId: Id | null;
  title: string;
  date: IsoDate;
  description: string | null;
  status: PublicationStatus;
  coverPhotoId: Id | null;
  /** Autoriser le téléchargement depuis l'app Parents. */
  allowDownload: boolean;
  /**
   * `participants` : album d'un camp ou d'un hike, réservé aux familles des
   * enfants inscrits pendant le séjour et publié jour par jour (voir
   * `camp-journal.ts`). `all` : tous les parents, publication classique.
   */
  audience: AlbumAudience;
  publishedAt: IsoDateTime | null;
  createdBy: Id;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  deletedAt: IsoDateTime | null;
}

export interface Photo {
  id: Id;
  albumId: Id;
  fileId: Id;
  caption: string | null;
  takenAt: IsoDateTime | null;
  /** Jour du séjour (`YYYY-MM-DD`) pour une photo d'album de camp/hike. */
  journalDay: IsoDate | null;
  order: number;
  createdAt: IsoDateTime;
  deletedAt: IsoDateTime | null;
}

export const DOCUMENT_KINDS = ['pdf', 'word', 'excel', 'powerpoint', 'image', 'other'] as const;
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];

export interface DocumentFolder {
  id: Id;
  name: string;
  parentId: Id | null;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

export interface DocumentVersion {
  fileId: Id;
  uploadedBy: Id;
  uploadedAt: IsoDateTime;
  note: string | null;
}

export interface DocumentItem {
  id: Id;
  title: string;
  description: string | null;
  fileId: Id;
  kind: DocumentKind;
  /** Publié dans l'onglet Fichiers de l'app Parents. */
  parentVisible: boolean;
  folderId: Id | null;
  tags: string[];
  versions: DocumentVersion[];
  createdBy: Id;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  deletedAt: IsoDateTime | null;
}

/** Favoris et consultations, par utilisateur. */
export interface DocumentFavorite {
  documentId: Id;
  userId: Id;
  createdAt: IsoDateTime;
}

export interface DocumentView {
  id: Id;
  documentId: Id;
  userId: Id;
  viewedAt: IsoDateTime;
}

// ---------------------------------------------------------------------------
// Communication parents
// ---------------------------------------------------------------------------

export type MessageStatus = 'draft' | 'scheduled' | 'published';

export interface ParentMessage {
  id: Id;
  subject: string;
  body: string;
  status: MessageStatus;
  important: boolean;
  scheduledAt: IsoDateTime | null;
  publishedAt: IsoDateTime | null;
  attachmentIds: Id[];
  activityId: Id | null;
  /** `null` = tous les parents ; sinon la liste des parents destinataires. */
  audienceParentIds: Id[] | null;
  createdBy: Id;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  deletedAt: IsoDateTime | null;
}

export interface MessageRead {
  messageId: Id;
  parentId: Id;
  readAt: IsoDateTime;
}

// ---------------------------------------------------------------------------
// Notifications, journal, corbeille
// ---------------------------------------------------------------------------

export const NOTIFICATION_TYPES = [
  'message_published',
  'activity_created',
  'activity_updated',
  'activity_cancelled',
  'photos_published',
  'rsvp_reminder',
  'rsvp_received',
  'task_assigned',
  'task_due',
  'task_status_changed',
  'task_comment',
  'invitation',
  'mention',
  'dev_ticket',
  'camp_journal_published',
  'camp_reminder',
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export type AudienceType = 'user' | 'parent';

export interface Notification {
  id: Id;
  audienceType: AudienceType;
  audienceId: Id;
  type: NotificationType;
  title: string;
  body: string | null;
  /** Route interne à ouvrir au clic, ex. `/calendrier/abc123`. */
  link: string | null;
  readAt: IsoDateTime | null;
  createdAt: IsoDateTime;
}

export interface AuditEntry {
  id: Id;
  actorType: AudienceType | 'system';
  actorId: Id | null;
  actorLabel: string;
  action: string;
  entity: string;
  entityId: Id | null;
  summary: string;
  /** Différentiel sérialisé (champs modifiés uniquement). */
  changes: Record<string, { from: unknown; to: unknown }> | null;
  ip: string | null;
  createdAt: IsoDateTime;
}

export interface TrashEntry {
  entity: string;
  entityId: Id;
  label: string;
  deletedAt: IsoDateTime;
  deletedBy: Id | null;
  /** Suppression définitive automatique après cette date. */
  purgeAt: IsoDateTime;
}

// ---------------------------------------------------------------------------
// Paramètres & tableau de bord
// ---------------------------------------------------------------------------

export type ThemeMode = 'light' | 'dark' | 'system';

export interface AppSettings {
  sectionName: string;
  unitName: string;
  logoFileId: Id | null;
  accentColor: string;
  themeMode: ThemeMode;
  /** Coordonnées utilisées par le widget météo. */
  weatherLatitude: number;
  weatherLongitude: number;
  weatherCity: string;
  /** Jours avant une réunion pour le rappel de présence aux parents. */
  rsvpReminderDaysBefore: number;
  /** Rétention de la corbeille en jours. */
  trashRetentionDays: number;
  /**
   * Onglet Uniformes visible dans l'app Staff. Désactivable temporairement
   * depuis Baygon pour ne pas polluer la navigation hors campagne.
   */
  uniformesStaffEnabled: boolean;
  /**
   * Onglet Uniformes visible dans l'app Parents. Indépendant du réglage staff.
   */
  uniformesParentsEnabled: boolean;
  /**
   * IBAN du compte de la section pour le règlement des uniformes.
   * Vide tant que le Baygon ne l’a pas encodé : l’application n’affiche alors
   * pas de numéro fictif. La forme stockée est groupée par 4 (`BE68 5390 …`).
   */
  bankIban: string;
  /**
   * Onglet Objets perdus visible dans l'app Staff. Même principe que pour les
   * uniformes : hors période, l'onglet n'a rien à dire et encombre une
   * navigation déjà longue.
   *
   * **Masquer ne supprime rien.** Ces deux drapeaux ne touchent qu'à la
   * visibilité : les objets, leurs photos et leurs réclamations restent en base
   * et reviennent intacts au rallumage. `lost_items` n'est d'ailleurs pas une
   * table à corbeille, donc aucune purge automatique ne la balaie.
   */
  objetsPerdusStaffEnabled: boolean;
  /**
   * Onglet Objets perdus visible dans l'app Parents. Indépendant du réglage
   * staff : on peut cesser de le montrer aux familles tout en gardant l'écran
   * côté animateurs pour déclarer le prochain gant trouvé.
   */
  objetsPerdusParentsEnabled: boolean;
  /** Onglet Camp/Hike des parents : jours d'ouverture avant le départ (Baygon). */
  campTabLeadDays: number;
  /** Onglet Camp/Hike des parents : jours de maintien après le retour (Baygon). */
  campTabTailDays: number;
  /**
   * Secret du lien d'abonnement au calendrier. Non modifiable directement : on
   * ne peut que le régénérer, ce qui révoque le lien précédent.
   */
  calendarFeedToken: string | null;
}

export const WIDGET_TYPES = [
  'next_activity',
  'urgent_tasks',
  'rsvp_forecast',
  'recent_messages',
  'recent_photos',
  'birthdays',
  'weather',
  'shortcuts',
  'attendance_summary',
  'finance_balance',
] as const;
export type WidgetType = (typeof WIDGET_TYPES)[number];

export interface WidgetPlacement {
  id: Id;
  type: WidgetType;
  /** Position dans la grille (ordre de rendu). */
  order: number;
  /** Largeur en colonnes de la grille à 4 colonnes. */
  span: 1 | 2 | 3 | 4;
  visible: boolean;
}

export interface DashboardLayout {
  userId: Id;
  widgets: WidgetPlacement[];
  updatedAt: IsoDateTime;
}

// ---------------------------------------------------------------------------
// Authentification
// ---------------------------------------------------------------------------

export interface AuthSession {
  token: string;
  expiresAt: IsoDateTime;
  audienceType: AudienceType;
}

/** Identité renvoyée par `GET /api/auth/me` pour l'app Staff. */
/**
 * État de la double authentification du compte connecté.
 *
 * `required` sans `enabled` : l'application conduit à l'écran d'enrôlement.
 * C'est un confort, pas une sécurité — le serveur refuse de son côté les routes
 * d'administration tant que l'enrôlement n'est pas fait.
 */
export interface MfaStatus {
  enabled: boolean;
  required: boolean;
  /** Enrôlement commencé mais pas confirmé par un premier code. */
  pending: boolean;
  backupCodesLeft: number;
}

export interface StaffIdentity {
  audienceType: 'user';
  user: User;
  role: Role;
  mfa: MfaStatus;
}

/** Identité renvoyée par `GET /api/auth/me` pour l'app Parents. */
export interface ParentIdentity {
  audienceType: 'parent' | 'user';
  parent: ParentAccount;
  children: Baladin[];
  isSuperAdmin?: boolean;
  /** Compte Staff « Chef d'Unité » en consultation : aucune écriture possible. */
  isChefUnite?: boolean;
}

export type Identity = StaffIdentity | ParentIdentity;

// ---------------------------------------------------------------------------
// Synchronisation temps réel
// ---------------------------------------------------------------------------

export type ChangeOp = 'created' | 'updated' | 'deleted' | 'restored';

/** Message diffusé par le serveur à tous les clients connectés. */
export interface ChangeEvent {
  entity: string;
  entityId: Id;
  op: ChangeOp;
  /** Auteur du changement, pour éviter de réappliquer son propre écho. */
  actorId: Id | null;
  at: IsoDateTime;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  offset: number;
  limit: number;
}

// ---------------------------------------------------------------------------
// Commandes d'uniformes
// ---------------------------------------------------------------------------

/**
 * Articles du catalogue fixe historique (avant les produits par campagne).
 * Conservé pour lire les anciennes commandes et proposer des modèles par défaut.
 */
export const UNIFORM_ARTICLES = ['pull', 'tshirt', 'ecusson'] as const;
export type UniformArticle = (typeof UNIFORM_ARTICLES)[number];

export const UNIFORM_ARTICLE_LABELS: Record<UniformArticle, string> = {
  pull: 'Pull',
  tshirt: 'T-shirt',
  ecusson: 'Écusson',
};

/**
 * Produit proposé dans une campagne : photo, tailles et prix définis par le staff.
 */
export interface UniformProduct {
  id: Id;
  campaignId: Id;
  name: string;
  photoFileId: Id | null;
  /** Tailles proposées aux parents (ex. `["6 ans","8 ans"]` ou `["unique"]`). */
  sizes: string[];
  unitPriceCents: number;
  sortOrder: number;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  deletedAt: IsoDateTime | null;
}

/**
 * Une ligne du panier. Le prix unitaire et le nom sont figés à la commande
 * pour que le total reste exact même si le tarif catalogue change plus tard.
 */
export interface UniformOrderItem {
  /** Produit de la campagne (commandes récentes). */
  productId?: Id;
  /** Libellé figé à la commande. */
  name?: string;
  /**
   * Article du catalogue fixe historique (`pull` / `tshirt` / `ecusson`).
   * Présent seulement sur les commandes passées avant les produits par campagne.
   */
  article?: UniformArticle;
  /** Ex. `"8 ans"`, `"10 ans"`, `"unique"` pour un écusson. */
  size: string;
  quantity: number;
  unitPriceCents: number;
  /** Vrai si cet article a été remis / retiré au local. */
  pickedUp?: boolean;
  /** Quantité remise si retrait partiel (ex. 1 sur 2). */
  pickedUpQuantity?: number;
}

/** Campagne de commande ouverte (ou clôturée) par le staff. */
export interface UniformCampaign {
  id: Id;
  name: string;
  /** Année utilisée dans la communication structurée de paiement. */
  year: number;
  /** Date civile `YYYY-MM-DD` — après celle-ci, le formulaire parents est verrouillé. */
  endDate: IsoDate;
  isActive: boolean;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  deletedAt: IsoDateTime | null;
}

/** Commande passée par un parent pour un baladin donné. */
export interface UniformOrder {
  id: Id;
  campaignId: Id;
  parentId: Id;
  baladinId: Id;
  items: UniformOrderItem[];
  totalPriceCents: number;
  createdAt: IsoDateTime;
  /** Moment où le parent a récupéré la commande ; `null` = pas encore retirée. */
  pickedUpAt: IsoDateTime | null;
  /** Animateur qui a coché le retrait. */
  pickedUpBy: Id | null;
}
